import { ChangeEvent, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'ramda'

import PlainLabel from 'app/common/ui-next/plain-label'
import { toFixedValueCurrency } from 'app/common/lib/toFixedValue'
import { useCurrencySign } from 'app/common/components-next/currency'
import ControlsBar from 'app/common/ui-next/controls-bar'
import PlainButton from 'app/common/ui-next/plain-button'
import { BankCardId } from 'app/dataTypes/acquiring/recurrent/cards'
import { AcquiringItem, RefillType } from 'app/dataTypes/acquiring/settings'
import { Loading } from 'app/common/templates-next/page-template'

import {
  useDeactivateSavedBankCard,
  useRecurrentSavedBankCards,
} from 'app/dataProviders/acquiring/recurring/hooks'

import { SavedCards } from './SavedCards'
// import { SaveCardControls } from './controls'
import { useAmountTotal } from '../useAmountTotal'
import { Stub } from './Stub'
import { AgreementToSave } from './AgreementToSave'

import styles from './RecurrentPayment.module.scss'

const formId = 'recurrentPaymentForm' as const

type Props = {
  amount: number
  acquiringItem: AcquiringItem
  refillType: RefillType
  onRecurrent: (data: { bankId: BankCardId } | { save: boolean } | {} | null) => void
  onPrev: () => void
  onNext: () => void
}

export const RecurrentPayment = ({
  amount,
  acquiringItem,
  refillType,
  onRecurrent,
  onPrev,
  onNext,
}: Props) => {
  const { t } = useTranslation('finances')
  const [selectedCardId, setSelectedCardId] = useState<null | BankCardId>(null)

  const currency = useCurrencySign()
  const amountTotal = useAmountTotal({ amount, acquiringItem })
  const amountText = toFixedValueCurrency(amountTotal, undefined, currency)
  const { id, ownerType } = acquiringItem
  const {
    data: savedBankCards,
    loading,
    requestRef,
  } = useRecurrentSavedBankCards({ params: { id, ownerType, refillType } })
  const { deactivate } = useDeactivateSavedBankCard()

  const handleSavedCardRemove = useCallback(
    async (bankCardId: BankCardId) => {
      const res = await deactivate({ bankCardId })
      if (res) {
        await requestRef.current()
      }
      return res
    },
    [deactivate, requestRef],
  )

  const handleSubmitNewWithSave = () => {
    onRecurrent({ save: true })
    onNext()
  }

  const handleSubmitNewWithoutSave = () => {
    onRecurrent({})
    onNext()
  }

  const handleSubmitSaved = (e: ChangeEvent<HTMLFormElement>) => {
    const { id: currentFormId } = e.target
    if (currentFormId !== formId) return
    e.preventDefault()
    onRecurrent({ bankId: selectedCardId })
    onNext()
  }

  const nothing = isEmpty(savedBankCards)

  const currentCard = useMemo(() => {
    if (selectedCardId) {
      const card = selectedCardId ? savedBankCards.find(({ id }) => id === selectedCardId) : null
      if (card) {
        return `${card.system.toUpperCase()} **** ${card.lastFourDigits}`
      }
    }
    return ''
  }, [savedBankCards, selectedCardId])

  return (
    <>
      <form id={formId} onSubmit={handleSubmitSaved}>
        {loading && <Loading className={styles.loading} />}

        {!loading && !nothing && (
          <SavedCards
            label={
              selectedCardId
                ? t('recurrent_refill_saved_cards', 'Сохраненные карты')
                : t('recurrent_refill_choose_card', 'Выберите карту')
            }
            savedBankCards={savedBankCards}
            onSavedCardRemove={handleSavedCardRemove}
            selectedCardId={selectedCardId}
            onChangeValue={setSelectedCardId}
          />
        )}
        {!loading && nothing && <Stub className={styles.stub} />}
        {/* <PlainCheckbox
          label={t('save_card_checkbox', 'Сохранить карту для будущих платежей')}
          name={SaveCardControls.SaveCard}
          disabled={loading}
          value="anyLetters"
        /> */}
        {!loading && !nothing && (
          <PlainLabel id="itsSafe" className={styles.itsSafe} skipUpperSpace displayAsBlock>
            {t('save_card_its_safe', 'Данные ваших карт в безопасности — мы никому их не передаем')}
          </PlainLabel>
        )}
        <PlainLabel id="amount" className={styles.amount} displayAsBlock>
          {`${t('save_card_payment_amount', 'К оплате')}: `}
          <span className={styles.amountText}>{amountText}</span>
        </PlainLabel>

        <ControlsBar className={styles.buttons} left>
          <PlainButton outline onClick={onPrev}>
            {t('back_bt', 'Назад')}
          </PlainButton>

          <AgreementToSave
            onAgree={handleSubmitNewWithSave}
            onDisagree={handleSubmitNewWithoutSave}
          >
            {(onClick) => (
              <PlainButton disabled={loading} outline={!nothing} onClick={onClick}>
                {t('recurrent_refill_bt_new_card', 'Оплатить новой картой')}
              </PlainButton>
            )}
          </AgreementToSave>

          {!loading && !nothing && (
            <PlainButton disabled={loading || !selectedCardId} type="submit">
              {selectedCardId || loading
                ? t('recurrent_refill_bt_saved_card', { card: currentCard }) // Оплатить картой {{card}}
                : t('recurrent_refill_choose_card', 'Выберите карту')}
            </PlainButton>
          )}
        </ControlsBar>
      </form>

      <div className={styles.space} />
    </>
  )
}
